/**
 * Admin › Indicações (D723, doc 29 gap G12, owner Q7) - the pure view-model behind the screen where operators run the referral campaigns of a market:
 *
 *  - "Convide motoristas" (`DriverReferralAdminApi`): an operator PROPOSES a campaign for one service category (the reward the rider gets as a coupon, the rides the
 *    invited driver must complete and in how many days, the deadline, the budget and the per-rider cap), a DIFFERENT operator approves or refuses it (four eyes - the
 *    screen hides the button from the proposer and says why; the server refuses `DRIVER_REFERRAL_SELF_APPROVAL` anyway); a change (deadline, budget, cap) is
 *    proposed and approved the same way; any operator holding the word ends an active one (it only stops new invitations);
 *  - "Indique um amigo" (`CouponAdminApi`): the rider-to-rider campaigns (D476) - listed, created, switched off or extended.
 *
 * Every bound a draft is checked against is the GOVERNED ceiling the server sends (`promotion.driverReferral` and the coupon envelope) - the screen never offers a value
 * past it and the server re-checks every one. Nothing here names a country, a currency, a category or an amount.
 */
import type { DriverReferralCampaignView, DriverReferralCeilingView, DriverReferralProposalInput } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type ReferralTone = 'neutral' | 'success' | 'warning' | 'danger';

export function referralAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true };
  return {
    mayView: grantsAllow(grants, { permission: 'campaign:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'campaign:manage', target: 'GEO' }),
  };
}

export function driverCampaignTone(status: DriverReferralCampaignView['status']): ReferralTone {
  return status === 'ACTIVE' ? 'success' : status === 'PROPOSED' ? 'warning' : status === 'REFUSED' ? 'danger' : 'neutral';
}

export type ApproveBlock = 'NOT_PENDING' | 'NO_PERMISSION' | 'OWN_PROPOSAL';

/** Why this operator may not decide a proposal (or undefined when he may). */
export function driverCampaignDecisionBlock(c: Pick<DriverReferralCampaignView, 'status' | 'proposedBy'>, operatorId: string | undefined, mayManage: boolean, selfApproval = false): ApproveBlock | undefined {
  if (c.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!mayManage) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && c.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** Why this operator may not decide a pending change (or undefined when he may). */
export function driverChangeDecisionBlock(c: Pick<DriverReferralCampaignView, 'pendingChange'>, operatorId: string | undefined, mayManage: boolean, selfApproval = false): ApproveBlock | undefined {
  if (c.pendingChange === undefined) return 'NOT_PENDING';
  if (!mayManage) return 'NO_PERMISSION';
  if (!selfApproval && operatorId !== undefined && c.pendingChange.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

export const driverCampaignCanEnd = (c: Pick<DriverReferralCampaignView, 'status'>, mayManage: boolean): boolean => mayManage && c.status === 'ACTIVE';
export const driverCampaignCanChange = (c: Pick<DriverReferralCampaignView, 'status' | 'pendingChange'>, mayManage: boolean): boolean => mayManage && c.status === 'ACTIVE' && c.pendingChange === undefined;

/** How much of the budget the open and rewarded referrals hold, in basis points (capped at 10 000). */
export function committedBp(c: Pick<DriverReferralCampaignView, 'committedMinor' | 'budgetMinor'>): number {
  const budget = /^\d+$/.test(c.budgetMinor) ? BigInt(c.budgetMinor) : 0n;
  const held = c.committedMinor !== undefined && /^\d+$/.test(c.committedMinor) ? BigInt(c.committedMinor) : 0n;
  return budget <= 0n ? 0 : Math.min(10_000, Number((held * 10_000n) / budget));
}

export interface DriverReferralDraft {
  readonly categoryCode: string;
  readonly name: string;
  /** How long the campaign takes invitations (the deadline counts from now). */
  readonly campaignDays: number | undefined;
  readonly ridesRequired: number | undefined;
  readonly completionWindowDays: number | undefined;
  /** The reward: a fixed-amount coupon, used `uses` times, valid `validDays` from the reward. */
  readonly rewardMinor: bigint | undefined;
  readonly uses: number | undefined;
  readonly validDays: number | undefined;
  readonly budgetMinor: bigint | undefined;
  readonly perReferrerCap: number | undefined;
}

export const EMPTY_DRIVER_REFERRAL_DRAFT: DriverReferralDraft = {
  categoryCode: '', name: '', campaignDays: undefined, ridesRequired: undefined, completionWindowDays: undefined, rewardMinor: undefined, uses: 1, validDays: undefined, budgetMinor: undefined, perReferrerCap: undefined,
};

export type DriverReferralDraftIssue =
  | 'CEILING_OFF' | 'CATEGORY_REQUIRED' | 'NAME_REQUIRED' | 'DAYS_REQUIRED' | 'DAYS_OVER_POLICY' | 'RIDES_REQUIRED' | 'RIDES_OVER_POLICY' | 'WINDOW_REQUIRED' | 'WINDOW_OVER_POLICY'
  | 'REWARD_REQUIRED' | 'USES_REQUIRED' | 'VALIDITY_REQUIRED' | 'BUDGET_REQUIRED' | 'BUDGET_OVER_POLICY' | 'BUDGET_BELOW_REWARD' | 'CAP_REQUIRED' | 'CAP_OVER_POLICY';

/** The coupon engine's own bounds of a reward (uses 1-100, validity 1-365 days) - the structural floor, not a business rule of the market. */
export const REWARD_USES_MAX = 100;
export const REWARD_VALID_DAYS_MAX = 365;

const positive = (n: number | undefined): n is number => n !== undefined && Number.isInteger(n) && n >= 1;

/** One referral's face value (what it commits from the budget): the amount times the uses. */
export const draftFaceMinor = (d: Pick<DriverReferralDraft, 'rewardMinor' | 'uses'>): bigint | undefined => (d.rewardMinor !== undefined && positive(d.uses) ? d.rewardMinor * BigInt(d.uses) : undefined);

/** How many rewards the budget covers (floor(budget / face)). */
export function rewardsCovered(d: Pick<DriverReferralDraft, 'rewardMinor' | 'uses' | 'budgetMinor'>): number | undefined {
  const face = draftFaceMinor(d);
  if (face === undefined || face <= 0n || d.budgetMinor === undefined || d.budgetMinor < face) return undefined;
  const n = d.budgetMinor / face;
  return n > 1_000_000n ? 1_000_000 : Number(n);
}

/** The first thing wrong with a draft against the ceiling in force, or undefined. The server re-checks every one. */
export function driverReferralDraftIssue(d: DriverReferralDraft, ceiling: DriverReferralCeilingView, categories: readonly string[]): DriverReferralDraftIssue | undefined {
  if (!ceiling.enabled || !ceiling.couponsEnabled || ceiling.currency === undefined) return 'CEILING_OFF';
  if (!categories.includes(d.categoryCode)) return 'CATEGORY_REQUIRED';
  if (d.name.trim() === '' || d.name.trim().length > 80) return 'NAME_REQUIRED';
  if (!positive(d.campaignDays)) return 'DAYS_REQUIRED';
  if (d.campaignDays > ceiling.maxCampaignDays) return 'DAYS_OVER_POLICY';
  if (!positive(d.ridesRequired)) return 'RIDES_REQUIRED';
  if (d.ridesRequired > ceiling.maxRidesRequired) return 'RIDES_OVER_POLICY';
  if (!positive(d.completionWindowDays)) return 'WINDOW_REQUIRED';
  if (d.completionWindowDays > ceiling.maxCompletionWindowDays) return 'WINDOW_OVER_POLICY';
  if (d.rewardMinor === undefined || d.rewardMinor <= 0n) return 'REWARD_REQUIRED';
  if (!positive(d.uses) || d.uses > REWARD_USES_MAX) return 'USES_REQUIRED';
  if (!positive(d.validDays) || d.validDays > REWARD_VALID_DAYS_MAX) return 'VALIDITY_REQUIRED';
  if (d.budgetMinor === undefined || d.budgetMinor <= 0n) return 'BUDGET_REQUIRED';
  if (/^\d+$/.test(ceiling.maxBudgetMinor) && d.budgetMinor > BigInt(ceiling.maxBudgetMinor)) return 'BUDGET_OVER_POLICY';
  const face = draftFaceMinor(d) ?? 0n;
  if (d.budgetMinor < face) return 'BUDGET_BELOW_REWARD';
  if (!positive(d.perReferrerCap)) return 'CAP_REQUIRED';
  if (d.perReferrerCap > ceiling.maxPerReferrerCap) return 'CAP_OVER_POLICY';
  return undefined;
}

/** The body of a proposal: the window starts now; an actor is never part of it (the session is); the currency is the coupon envelope's. */
export function driverReferralProposalBody(marketId: string, d: DriverReferralDraft, currency: string, nowIso: string): DriverReferralProposalInput {
  return {
    marketId,
    categoryCode: d.categoryCode,
    name: d.name.trim(),
    validFrom: nowIso,
    validTo: new Date(Date.parse(nowIso) + (d.campaignDays ?? 0) * 86_400_000).toISOString(),
    ridesRequired: d.ridesRequired ?? 0,
    completionWindowDays: d.completionWindowDays ?? 0,
    reward: { discountKind: 'FIXED', fixedMinor: (d.rewardMinor ?? 0n).toString(), currency, validDays: d.validDays ?? 0, uses: d.uses ?? 0, paymentInstruments: ['cash'], ignoreMinimumFare: false },
    budgetMinor: (d.budgetMinor ?? 0n).toString(),
    perReferrerCap: d.perReferrerCap ?? 0,
  };
}

/** A rider-to-rider campaign draft (D476): two fixed-amount coupons, the friend's at once and the inviter's after the friend's rides. */
export interface RiderReferralDraft {
  readonly name: string;
  readonly campaignDays: number | undefined;
  readonly ridesRequired: number | undefined;
  readonly newUserWindowDays: number | undefined;
  readonly referrerMinor: bigint | undefined;
  readonly refereeMinor: bigint | undefined;
  readonly validDays: number | undefined;
}

export const EMPTY_RIDER_REFERRAL_DRAFT: RiderReferralDraft = { name: '', campaignDays: undefined, ridesRequired: undefined, newUserWindowDays: undefined, referrerMinor: undefined, refereeMinor: undefined, validDays: undefined };

export function riderReferralDraftComplete(d: RiderReferralDraft): boolean {
  return d.name.trim() !== '' && positive(d.campaignDays) && positive(d.ridesRequired) && positive(d.newUserWindowDays) && d.referrerMinor !== undefined && d.referrerMinor > 0n && d.refereeMinor !== undefined && d.refereeMinor > 0n && positive(d.validDays);
}

/** A percent in basis points as operators read it: 1500 -> "15%", 1250 -> "12.5%". */
export function referralPercentLabel(basisPoints: number): string {
  return `${basisPoints % 100 === 0 ? basisPoints / 100 : (basisPoints / 100).toFixed(1)}%`;
}

/**
 * The new deadline of a rider-to-rider campaign, `days` whole days from now (the PATCH `validTo` the server accepts) - undefined while the days are not a positive
 * whole number, so the button stays off.
 */
export function riderDeadlineAfter(days: number | undefined, nowIso: string): string | undefined {
  return positive(days) ? new Date(Date.parse(nowIso) + days * 86_400_000).toISOString() : undefined;
}

export function riderReferralBody(marketId: string, d: RiderReferralDraft, currency: string, nowIso: string): Record<string, unknown> {
  const benefit = (minor: bigint | undefined) => ({ discountKind: 'FIXED', fixedMinor: (minor ?? 0n).toString(), currency, validDays: d.validDays ?? 0, uses: 1, paymentInstruments: ['cash'], ignoreMinimumFare: false });
  return {
    marketId,
    name: d.name.trim(),
    validFrom: nowIso,
    validTo: new Date(Date.parse(nowIso) + (d.campaignDays ?? 0) * 86_400_000).toISOString(),
    ridesRequired: d.ridesRequired ?? 0,
    newUserWindowDays: d.newUserWindowDays ?? 0,
    referrerBenefit: benefit(d.referrerMinor),
    refereeBenefit: benefit(d.refereeMinor),
  };
}
